import { ValidationError } from '@domain/errors';
import type { Company, CompanyDraft } from '@domain/entities';
import type { CompanyCreateInput, CompanyRepository, CompanyQuery } from '@domain/repositories';
import type { ModerationStatus, Page } from '@domain/value-objects';
import type { AccessPolicy } from '../../ports';
import type { UseCase } from '../use-case';

export class SearchCompanies implements UseCase<CompanyQuery, Page<Company>> {
  constructor(private readonly repo: CompanyRepository) {}
  execute(query: CompanyQuery): Promise<Page<Company>> {
    return this.repo.search(query);
  }
}

export class GetCompany implements UseCase<string, Company> {
  constructor(private readonly repo: CompanyRepository) {}
  execute(id: string): Promise<Company> {
    return this.repo.findById(id);
  }
}

export interface ModerateCompaniesInput {
  ids: readonly string[];
  status: Extract<ModerationStatus, 'approved' | 'rejected'>;
}

/** Approbation / rejet, unitaire ou en masse (même chemin métier). */
export class ModerateCompanies implements UseCase<ModerateCompaniesInput, Company[]> {
  constructor(private readonly repo: CompanyRepository) {}

  async execute({ ids, status }: ModerateCompaniesInput): Promise<Company[]> {
    if (ids.length === 0) {
      throw new ValidationError('Aucun compte sélectionné');
    }
    return this.repo.setStatus(ids, status);
  }
}

export class CreateCompany implements UseCase<CompanyCreateInput, Company> {
  constructor(private readonly repo: CompanyRepository) {}

  async execute(input: CompanyCreateInput): Promise<Company> {
    const fields: Record<string, string> = {};
    if (!input.name.trim()) fields.name = 'Le nom est requis';
    if (!input.ownerId) fields.ownerId = 'Le propriétaire est requis';
    if (!input.contactChannel) fields.contactChannel = 'Le canal de contact est requis';
    if (!input.contactDestination.trim()) fields.contactDestination = 'La destination de contact est requise';
    if (Object.keys(fields).length > 0) {
      throw new ValidationError('Formulaire incomplet', fields);
    }
    return this.repo.create({ ...input, name: input.name.trim() });
  }
}

export interface UpdateCompanyInput {
  id: string;
  draft: CompanyDraft;
}

export class UpdateCompany implements UseCase<UpdateCompanyInput, Company> {
  constructor(
    private readonly repo: CompanyRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ id, draft }: UpdateCompanyInput): Promise<Company> {
    if (!draft.name.trim()) {
      throw new ValidationError('Formulaire incomplet', { name: 'Le nom est requis' });
    }
    void this.policy;
    return this.repo.update(id, { ...draft, name: draft.name.trim() });
  }
}

export class DeleteCompany implements UseCase<string, void> {
  constructor(
    private readonly repo: CompanyRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer une entreprise');
    return this.repo.remove(id);
  }
}
